import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { AccessTime, CheckCircle, Edit, KingBed, RoomOutlined, SettingsApplications } from "@material-ui/icons";
import HistoryIcon from "@material-ui/icons/History";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { CalendarSync, Check, PlayCircle, X } from "lucide-react";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import moment from "moment";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import React, { useCallback, useContext, useMemo, useState } from "react";
import useFillStore from "../../hooks/useFillStore";
import useFormatNumber from "../../hooks/useFormatNumber";
import { StoreContext } from "../../store";
import {
  SUBSCRIPTION_CHANGE_STATUS,
  SUBSCRIPTION_CHANGE_TYPE,
  SUBSCRIPTION_PLANS,
  SUBSCRIPTION_STATUS,
} from "../../utils/constants/subscription.constant";
import { capitalizeFirstLetter } from "../../utils/formatting/string";
import NoDataFound from "../misc/NoData";
import { SKButton } from "../ui";
import useConfirmDialog from "../ConfirmDialog";
import AccountExpiredAlert from "./alerts/AccountExpiredAlert";
import { Alert, AlertTitle } from "@material-ui/lab";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(0.5),
  },
  cardBase: {
    position: "relative",
    height: "100%",
    // borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  hoverableCard: {
    backgroundColor: "#fff",
    boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
    "&:hover": {
      // transform: "translateY(-2px)",
      boxShadow: "0 10px 20px rgba(0,0,0,0.06)",
      borderColor: theme.palette.primary.main,
    },
  },
  activePlanCard: {
    border: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: "#fff",
  },
  staticCard: {
    backgroundColor: "#fafafa",
    boxShadow: "none",
  },
  cardHeader: {
    padding: theme.spacing(1.5, 2, 0, 2),
  },
  cardTitle: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.02em",
  },
  cardContent: {
    padding: theme.spacing(2),
    "&:last-child": {
      paddingBottom: theme.spacing(2),
    },
  },
  cardHeaderAction: {
    margin: 0,
  },
  planName: {
    fontSize: "1.25rem",
    fontWeight: 800,
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
  planPrice: {
    fontSize: "1rem",
    fontWeight: 700,
    display: "flex",
    alignItems: "baseline",
    gap: 4,
    marginTop: theme.spacing(0.5),
  },
  priceInterval: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },
  statusChip: {
    height: 20,
    borderRadius: 4,
    fontWeight: 700,
    fontSize: "0.65rem",
    textTransform: "uppercase",
  },
  activeChip: {
    backgroundColor: "#E8F5E9",
    color: "#2E7D32",
    border: "1px solid #C8E6C9",
  },
  expireChip: {
    backgroundColor: "#FFF3E0",
    color: "#EF6C00",
    border: "1px solid #FFE0B2",
  },
  usageSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: "#fff",
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  usageLabel: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  progressBarRoot: {
    height: 8,
    borderRadius: 4,
    backgroundColor: "#e2e8f0",
    overflow: "hidden",
  },
  progressBarValue: {
    height: "100%",
    borderRadius: 4,
    backgroundColor: theme.palette.primary.main,
    transition: "width 1s ease-in-out",
  },
  infoList: {
    marginTop: theme.spacing(1.5),
    padding: 0,
    listStyle: "none",
  },
  infoItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 0),
    borderBottom: `1px dashed ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  infoIcon: {
    color: theme.palette.text.disabled,
    fontSize: "1rem",
  },
  infoText: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  infoValue: {
    fontSize: "0.8rem",
    fontWeight: 600,
    marginLeft: "auto",
  },
  pendingContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  changeCard: {
    border: `1px solid ${theme.palette.warning.light}`,
    backgroundColor: "#FFFDE7",
    padding: theme.spacing(1.5),
    borderRadius: 8,
  },
  kpiValue: { fontWeight: 800, fontSize: 24 },
  sectionTitle: {
    fontSize: "0.75rem",
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.02em",
  },
  featureList: {
    marginTop: theme.spacing(1.5),
    padding: 0,
  },
  featureItem: {
    padding: theme.spacing(0.5, 0),
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
  },
  featureIcon: {
    marginTop: 2,
    fontSize: "0.8rem",
    color: theme.palette.text.disabled,
  },
  featureIncluded: {
    color: theme.palette.success.main,
  },
  featureExcluded: {
    color: theme.palette.text.disabled,
  },
  featureText: {
    fontSize: "0.8rem",
    lineHeight: 1.4,
  },
  planBadge: {
    position: "absolute",
    top: 4,
    right: 12,
    zIndex: 2,
  },
  activeIndicator: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.65rem",
    fontWeight: 900,
    color: "#fff",
    backgroundColor: theme.palette.primary.main,
    padding: "2px 10px",
    borderRadius: 100,
    marginBottom: 8,
    width: "fit-content",
    textTransform: "uppercase",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  radioCard: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    cursor: "pointer",
    marginBottom: theme.spacing(1.5),
    transition: "all 0.2s ease-in-out",
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    "&:hover": {
      borderColor: theme.palette.primary.main,
      // backgroundColor: "#fcfcfc",
    },
  },
  radioCardSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: "#FFFDE7",
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
  },
  radioIcon: {
    color: theme.palette.primary.main,
    marginTop: 2,
  },
  radioContent: {
    flex: 1,
  },
  radioTitle: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    marginBottom: 4,
  },
  radioDescription: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  warningNote: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: "#FFF3E0",
    borderRadius: 8,
    border: "1px solid #FFE0B2",
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
  },
  warningIcon: {
    color: "#EF6C00",
    fontSize: "1.2rem",
  },
  warningText: {
    fontSize: "0.8rem",
    color: "#E65100",
    lineHeight: 1.4,
  },
  bonusBoxContainer: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  bonusBox: {
    flex: 1,
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: "#fafafa",
    textAlign: "center",
  },
  bonusBoxTitle: {
    fontWeight: 700,
    fontSize: "0.75rem",
  },
  bonusBoxHighlight: {
    color: theme.palette.success.main,
    fontWeight: 700,
    fontSize: "0.7rem",
  },
  bonusBoxPrice: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
}));

async function fetchData(store) {
  const results = await Promise.all([
    store.subscription.fetchOne(store.subscription.selected?.id),
    store.subscription.fetchChanges(store.subscription.selected?.id),
    store.subscription.fetchPendingChange(store.subscription.selected?.id),
    store.subscription.fetchActiveInvoice(store.subscription.selected?.id, {
      changeIds: store.subscription.change?.pending ? [store.subscription.change.pending.id] : [],
    }),
  ]);

  return results;
}

const SummaryPanel = () => {
  const { t } = useTranslation("common");
  const formatNumber = useFormatNumber();

  const router = useRouter();
  const classes = useStyles();
  const store = useContext(StoreContext);

  const [fetching] = useFillStore(fetchData);

  const [ConfirmCancelChangeDialog, setChangeToCancel, changeToCancel] = useConfirmDialog();

  const [showModalRenewal, setShowModalRenewal] = useState(false);
  const [renewalOption, setRenewalOption] = useState("upgrade");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { selected, change, invoice } = store.subscription;

  const handleApplyRenewal = useCallback(async () => {
    if (renewalOption === "upgrade") {
      setShowModalRenewal(false);
      router.push(`/subscription/manage-plan`);
    } else if (renewalOption === "renew") {
      setIsSubmitting(true);
      try {
        const { status } = await store.subscription.generateActiveInvoice(store.subscription.selected?.id);
        if (status === 200) {
          store.pushToastMessage({
            message: t("Renewal invoice generated successfully"),
            severity: "success",
          });
          setShowModalRenewal(false);
        } else {
          store.pushToastMessage({
            message: t("Failed to generate renewal invoice"),
            severity: "error",
          });
        }
      } finally {
        setIsSubmitting(false);
        router.push(`/subscription?tab=billing`);
      }
    }
  }, [renewalOption, router, store, t]);

  // const handleManage
  const handleGenerateActiveInvoice = async () => {
    try {
      const { status, data } = await store.subscription.generateActiveInvoice(selected.id);
      if (status === 200) {
        store.pushToastMessage({
          message: t("Active invoice generated successfully"),
          severity: "success",
        });
      } else {
        store.pushToastMessage({
          message: t("Failed to generate active invoice. Please try again."),
          severity: "error",
        });
      }
    } finally {
      router.push(`/subscription?tab=billing`);
      // store.subscription.fetchActiveInvoice(store.subscription.selected.id, {
      //   changeIds: store.subscription.change?.pending ? [store.subscription.change.pending.id] : [],
      // });
    }
  };

  const handleCancelChange = useCallback(() => {
    setChangeToCancel(change?.pending);
  }, [setChangeToCancel, change?.pending]);

  const onCancelChange = useCallback(async () => {
    if (!changeToCancel) {
      return;
    }
    const response = await store.subscription.cancelChange(changeToCancel.subscriptionId, changeToCancel.id);
    if (response.status === 200) {
      store.pushToastMessage({
        message: t("Success cancel change plan"),
        severity: "success",
      });
    }
    setChangeToCancel(false);

    // reload page
    router.push(`/subscription`);
  }, [setChangeToCancel, changeToCancel, router, store, t]);

  const renderPlanCard = (plan) => {
    const isActive = selected?.plan?.toLowerCase() === plan.name?.toLowerCase();
    const isPending = change?.pending?.planName?.toLowerCase() === plan.name?.toLowerCase();

    return (
      <Grid item xs={12} sm={6} md={4} key={plan.key}>
        <Card
          className={`${classes.cardBase} ${classes.hoverableCard} ${isActive ? classes.activePlanCard : ""}`}
          elevation={0}
        >
          {plan.badge && (
            <Box className={classes.planBadge}>
              <Chip
                label={plan.badge}
                size="small"
                style={{
                  height: 20,
                  fontWeight: 800,
                  fontSize: "0.6rem",
                  textTransform: "uppercase",
                  backgroundColor: "#FFD140",
                  color: "#000",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  border: "none",
                }}
              />
            </Box>
          )}
          <CardHeader
            title={
              <Box>
                {isActive && (
                  <Box className={classes.activeIndicator}>
                    <CheckCircle style={{ fontSize: 12 }} />
                    {t("CURRENT PLAN")}
                  </Box>
                )}
                <Typography className={classes.planName}>{plan.name}</Typography>
              </Box>
            }
            subheader={
              <Typography className={classes.planPrice}>
                {plan.key === "starter" ? (
                  <>{t("Free")}</>
                ) : (
                  <>
                    {formatNumber(plan.price, "currency")}
                    <span className={classes.priceInterval}>/{t("room")}</span>
                    <span className={classes.priceInterval}>/{t("month")}</span>
                  </>
                )}
              </Typography>
            }
            className={classes.cardHeader}
          />
          <CardContent className={classes.cardContent}>
            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
              {t(plan.tagline)}
            </Typography>

            <Divider />

            <List className={classes.featureList} dense>
              {plan.features.map((feature, idx) => (
                <Box key={idx} className={classes.featureItem}>
                  {feature.included ? (
                    <Check size={14} className={`${classes.featureIcon} ${classes.featureIncluded}`} />
                  ) : (
                    <X size={14} className={`${classes.featureIcon} ${classes.featureExcluded}`} />
                  )}
                  <Typography
                    className={`${classes.featureText} ${!feature.included ? classes.featureExcluded : ""}`}
                    color={feature.included ? "textPrimary" : "textSecondary"}
                  >
                    {t(feature.name)}
                  </Typography>
                </Box>
              ))}
            </List>

            {plan.key !== "starter" && (
              <Box className={classes.bonusBoxContainer}>
                <Box className={classes.bonusBox}>
                  <Typography className={classes.bonusBoxTitle}>6 {t("Month")}</Typography>
                  <Typography className={classes.bonusBoxHighlight}>{t("1 Month Bonus")}</Typography>
                  <Typography className={classes.bonusBoxPrice}>
                    {formatNumber(plan.price * 6, "currency")} / {t("room")}
                  </Typography>
                </Box>
                <Box className={classes.bonusBox}>
                  <Typography className={classes.bonusBoxTitle}>12 {t("Month")}</Typography>
                  <Typography className={classes.bonusBoxHighlight}>{t("3 Month Bonus")}</Typography>
                  <Typography className={classes.bonusBoxPrice}>
                    {formatNumber(plan.price * 12, "currency")} / {t("room")}
                  </Typography>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <React.Fragment>
      <Box className={classes.root}>
        {/* Alert Section for Renewal */}
        {(store.subscription?.willExpireSoon || store.subscription?.isAccessExpired) && (
          <>
            <AccountExpiredAlert
              title={
                store.subscription?.willExpireSoon
                  ? t("Your subscription will be expired in {{count}} days", {
                      count: moment(selected?.period?.end).startOf("day").diff(moment().startOf("day"), "days"),
                    })
                  : null
              }
              description={
                store.subscription?.willExpireSoon
                  ? t(
                      "There's still {{count}} days left before your subscription expires. Renew now to keep using all features.",
                      {
                        count: moment(selected?.period?.end).startOf("day").diff(moment().startOf("day"), "days"),
                      },
                    )
                  : null
              }
              buttonLabel={t("Renew")}
              buttonDisabled={!!change?.pending}
              onAction={() => setShowModalRenewal(true)}
            />

            <Dialog
              open={showModalRenewal}
              onClose={() => !isSubmitting && setShowModalRenewal(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>{t("Extend Subscription")}</DialogTitle>
              <DialogContent>
                <Box
                  className={`${classes.radioCard} ${renewalOption === "upgrade" ? classes.radioCardSelected : ""}`}
                  onClick={() => setRenewalOption("upgrade")}
                >
                  <div className={classes.radioIcon}>
                    {renewalOption === "upgrade" ? (
                      <CheckCircle fontSize="small" />
                    ) : (
                      <div style={{ width: 20, height: 20, borderRadius: "50%", border: "2px solid #ccc" }} />
                    )}
                  </div>
                  <div className={classes.radioContent}>
                    <Typography className={classes.radioTitle}>{t("Upgrade Plan")}</Typography>
                    <Typography className={classes.radioDescription}>
                      {t("Explore our higher-tier plans with more features and capacity.")}
                    </Typography>
                  </div>
                </Box>

                {/* Disabled this option if current user plan is trial or starter */}
                <Box
                  className={`${classes.radioCard} ${renewalOption === "renew" ? classes.radioCardSelected : ""}`}
                  onClick={() => {
                    if (!store.subscription.isTrialPlan && !store.subscription.isStarterPlan) {
                      setRenewalOption("renew");
                    }
                  }}
                  style={
                    store.subscription.isTrialPlan || store.subscription.isStarterPlan
                      ? { opacity: 0.5, cursor: "not-allowed", borderColor: "#ccc" }
                      : {}
                  }
                >
                  <div className={classes.radioIcon}>
                    {renewalOption === "renew" ? (
                      <CheckCircle fontSize="small" />
                    ) : (
                      <div style={{ width: 20, height: 20, borderRadius: "50%", border: "2px solid #ccc" }} />
                    )}
                  </div>
                  <div className={classes.radioContent}>
                    <Typography className={classes.radioTitle}>
                      {t("Renew Current Plan")}
                      {(store.subscription.isTrialPlan || store.subscription.isStarterPlan) && (
                        <Chip
                          size="small"
                          label={t("Unavailable")}
                          style={{ marginLeft: 8, height: 20, fontSize: "0.7rem" }}
                        />
                      )}
                    </Typography>
                    <Typography className={classes.radioDescription}>
                      {t("Continue with your current plan and generate a renewal invoice.")}
                    </Typography>
                  </div>
                </Box>

                {renewalOption === "renew" && (
                  <Box className={classes.warningNote}>
                    <InfoOutlinedIcon className={classes.warningIcon} />
                    <Typography className={classes.warningText}>
                      {t("Note")}:{" "}
                      {t(
                        "Applying this option will immediately generate a new invoice for your renewal and create a pending subscription change.",
                      )}
                    </Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowModalRenewal(false)} color="primary" disabled={isSubmitting}>
                  {t("Cancel")}
                </Button>
                <Button onClick={handleApplyRenewal} color="primary" variant="contained" disabled={isSubmitting}>
                  {isSubmitting ? t("Applying...") : t("Apply")}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Card className={`${classes.cardBase} ${classes.staticCard}`} elevation={0}>
              <CardHeader
                title={<Typography className={classes.cardTitle}>{t("Current Subscription")}</Typography>}
                className={classes.cardHeader}
                action={
                  <SKButton
                    variant="contained"
                    size="small"
                    onClick={() => router.push(`/subscription/manage-plan`)}
                    disabled={!!change?.pending}
                  >
                    {t("Manage Plan")}
                  </SKButton>
                }
                classes={{
                  action: classes.cardHeaderAction,
                }}
              />
              <CardContent className={classes.cardContent}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography className={classes.planName}>{selected?.name}</Typography>

                    {!store.subscription.isTrialPlan && (
                      <Typography className={classes.planPrice}>
                        {store.subscription.isStarterPlan || !selected?.billing ? (
                          t("Free")
                        ) : (
                          <>
                            {formatNumber(selected?.billing.amount, "currency")}
                            <span className={classes.priceInterval}>
                              / {selected?.billing.cycle} {t(selected?.billing.frequency)}
                            </span>
                          </>
                        )}
                      </Typography>
                    )}
                  </Box>
                  {selected?.status && (
                    <Chip
                      label={capitalizeFirstLetter(selected?.status)}
                      className={`${classes.statusChip} ${
                        selected?.status === SUBSCRIPTION_STATUS.ACTIVE ? classes.activeChip : classes.expireChip
                      }`}
                    />
                  )}
                </Box>

                <ul className={classes.infoList}>
                  <li className={classes.infoItem}>
                    <KingBed size={16} className={classes.infoIcon} />
                    <Typography className={classes.infoText}>{t("Purchased Rooms")}</Typography>
                    <Typography component="div" className={classes.infoValue}>
                      {/* {store.subscription.isStarterPlan || !selected?.usage?.activeRoom ? (
                        "-"
                      ) : (
                        <>
                          <span>{t("{{count}} rooms", { count: selected?.usage.activeRoom })}</span>
                          <IconButton size="small" onClick={() => router.push(`/subscription/${store.subscription.selected?.id}/manage-room`)}>
                            <Edit fontSize="small" className={classes.infoIcon} />
                          </IconButton>
                        </>
                      )} */}
                      {store.subscription.isStarterPlan || !selected?.usage?.activeRoom
                        ? "-"
                        : t("{{count}} rooms", { count: selected?.usage.activeRoom })}
                    </Typography>
                  </li>
                  <li className={classes.infoItem}>
                    <PlayCircle size={16} className={classes.infoIcon} />
                    <Typography className={classes.infoText}>{t("Start Date")}</Typography>
                    <Typography className={classes.infoValue}>
                      {moment(selected?.period.start).isValid() ? moment(selected?.period.start).format("LL") : "-"}
                    </Typography>
                  </li>
                  <li className={classes.infoItem}>
                    <CalendarSync size={16} className={classes.infoIcon} />
                    <Typography className={classes.infoText}>{t("Renewal Date")}</Typography>
                    <Typography className={classes.infoValue}>
                      {store.subscription.isStarterPlan || !moment(selected?.period.start).isValid()
                        ? "-"
                        : moment(selected?.period.end).format("LL")}
                    </Typography>
                  </li>
                  <li className={classes.infoItem}>
                    <AccessTime className={classes.infoIcon} />
                    <Typography className={classes.infoText}>{t("Days Remaining")}</Typography>
                    <Typography className={classes.infoValue}>
                      {store.subscription.isStarterPlan || !selected?.remainingTime
                        ? "\u221E"
                        : selected.status === "expired"
                        ? "-"
                        : t("{{count}} days", { count: selected?.remainingTime })}
                    </Typography>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card className={`${classes.cardBase} ${classes.staticCard}`} elevation={0}>
              <CardHeader
                title={<Typography className={classes.cardTitle}>{t("Status & Updates")}</Typography>}
                action={
                  change?.pending && (
                    <SKButton variant="contained" size="small" color="secondary" onClick={handleCancelChange}>
                      {t("Cancel Change")}
                    </SKButton>
                  )
                  // <SKButton variant="contained" size="small" onClick={handleCancelChange}>
                  //   {t("Renew")}
                  // </SKButton>
                }
                className={classes.cardHeader}
                classes={{
                  action: classes.cardHeaderAction,
                }}
              />
              <CardContent className={classes.cardContent}>
                {change?.pending ? (
                  <Box className={classes.pendingContainer}>
                    <Box className={classes.changeCard}>
                      {/* <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <HistoryIcon color="primary" fontSize="small" />
                        <Typography variant="body2" style={{ fontWeight: 800, color: "#856404" }}>
                          {t(SUBSCRIPTION_CHANGE_TYPE[change?.pending.type])}
                        </Typography>
                      </Box> */}

                      <Typography component="div" variant="body2" style={{ fontSize: "0.85rem", color: "#856404" }}>
                        {t("Your plan will be updated to")}:
                        <ul>
                          <li>
                            {t("Plan")} : <strong>{capitalizeFirstLetter(change?.pending.toPlan)}</strong>
                          </li>
                          {change?.pending.toPlan !== "starter" && (
                            <>
                              <li>
                                {t("Billing")} :{" "}
                                <strong>
                                  {`${change?.pending.metadata.billing.cycle} ${t(
                                    change?.pending.metadata.billing.frequency,
                                  )}`}
                                </strong>
                              </li>
                              <li>
                                {t("Purchased Rooms")} :{" "}
                                <strong>{t("{{count}} rooms", { count: change?.pending.metadata.activeRooms })}</strong>
                              </li>
                              {/* <li>
                                {t("Price")} :{" "}
                                <strong>{formatNumber(change?.pending?.metadata?.newCycleTotal, "currency")}</strong>
                              </li> */}
                            </>
                          )}
                        </ul>
                        {/* <br /> */}
                        <span style={{ opacity: 0.7, fontSize: "0.75rem" }}>
                          {t("Effective on")}{" "}
                          {store.subscription?.isAccessExpired
                            ? moment(change?.pending.requestedAt).format("LL")
                            : moment(change?.pending.effectiveAt).format("LL")}
                        </span>
                      </Typography>

                      <Box textAlign="right">
                        {/* <SKButton
                          variant="contained"
                          size="small"
                          onClick={() => router.push(`/subscription/manage-delayed-disable`)}
                        >
                          {t("Manage Rooms")}
                        </SKButton> */}
                        {!invoice?.active && (
                          <SKButton variant="contained" size="small" onClick={handleGenerateActiveInvoice}>
                            {t("Generate Invoice")}
                          </SKButton>
                        )}
                      </Box>
                    </Box>

                    {["downgrade", "downsize"].includes(change?.pending.type) && (
                      <Box display="flex" gap={1} mt={1}>
                        <InfoOutlinedIcon style={{ fontSize: 16, color: "#1976d2" }} />
                        <Typography variant="caption" color="textSecondary" style={{ fontSize: "0.75rem" }}>
                          {store.subscription?.isAccessExpired
                            ? t("Changes are applied at your requested time.")
                            : t("Changes are applied at the end of your current billing cycle.")}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box textAlign="center" py={4}>
                    <NoDataFound
                      title={"No Pending Changes"}
                      description={"Your account is up to date."}
                      variant="small"
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography className={classes.sectionTitle}>
          <CheckCircle fontSize="small" color="primary" />
          {t("Available Plans & Features")}
        </Typography>

        <Alert
          severity="info"
          variant="outlined"
          style={{ marginBottom: 24, marginTop: 16, backgroundColor: "rgb(232, 244, 253)" }}
        >
          <AlertTitle style={{ fontSize: "0.9rem", fontWeight: 700 }}>{t("Bonus Validity Period")}:</AlertTitle>
          <ul style={{ margin: "6px 0", paddingLeft: 16 }}>
            <li>{t("Subscribe for 6 months and get 1 month bonus")}</li>
            <li>{t("Subscribe for 12 months and get 3 months bonus")}</li>
          </ul>
          {t("Enjoy an extended validity period for every 6-month and 12-month plan you choose.")}
        </Alert>

        <Grid container spacing={3}>
          {SUBSCRIPTION_PLANS.map((plan) => renderPlanCard(plan))}
        </Grid>
      </Box>

      <ConfirmCancelChangeDialog
        title={t("Cancel Change")}
        subTitle={t("Are you sure you want to cancel this change?")}
        onConfirm={onCancelChange}
      />
    </React.Fragment>
  );
};

export default observer(SummaryPanel);
